import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import PropertyCard from './components/PropertyCard';
import { useProperties } from '@/hooks/useProperties';
import { uniqueSorted } from '@/lib/uniqueSorted';

export default function Imoveis() {
  const navigate = useNavigate();
  const { properties: allProperties } = useProperties();
  const [searchParams] = useSearchParams();
  const pricePreset = searchParams.get('valor') ?? '';
  const initialPrice = (() => {
    if (pricePreset.startsWith('Até')) return { minPrice: '', maxPrice: '200000' };
    if (pricePreset.startsWith('R$ 200.000')) return { minPrice: '200000', maxPrice: '500000' };
    if (pricePreset.startsWith('R$ 500.000')) return { minPrice: '500000', maxPrice: '1000000' };
    if (pricePreset.startsWith('Acima')) return { minPrice: '1000000', maxPrice: '' };
    return { minPrice: '', maxPrice: '' };
  })();
  const [filters, setFilters] = useState({
    type: searchParams.get('tipo') ?? '',
    purpose: searchParams.get('finalidade') ?? '',
    city: searchParams.get('cidade') ?? '',
    neighborhood: searchParams.get('bairro') ?? '',
    minPrice: initialPrice.minPrice,
    maxPrice: initialPrice.maxPrice,
  });
  const [sortBy, setSortBy] = useState('relevance');

  const filteredProperties = useMemo(() => {
    let result = [...allProperties];

    if (filters.type) {
      result = result.filter((p) => p.type === filters.type);
    }
    if (filters.purpose) {
      result = result.filter((p) => p.purpose === filters.purpose);
    }
    if (filters.city) {
      result = result.filter((p) => p.city === filters.city);
    }
    if (filters.neighborhood) {
      result = result.filter((p) => p.neighborhood === filters.neighborhood);
    }
    if (filters.minPrice) {
      result = result.filter((p) => p.priceValue >= Number(filters.minPrice));
    }
    if (filters.maxPrice) {
      result = result.filter((p) => p.priceValue <= Number(filters.maxPrice));
    }

    if (sortBy === 'price-asc') {
      result.sort((a, b) => a.priceValue - b.priceValue);
    } else if (sortBy === 'price-desc') {
      result.sort((a, b) => b.priceValue - a.priceValue);
    }

    return result;
  }, [allProperties, filters, sortBy]);

  const types = ['Casa', 'Apartamento', 'Terreno', 'Sala Comercial', 'Chácara'];
  const purposes = ['Venda', 'Locação'];
  const cities = useMemo(() => uniqueSorted(allProperties.map((p) => p.city)), [allProperties]);
  const neighborhoods = useMemo(() => {
    const source = filters.city ? allProperties.filter((p) => p.city === filters.city) : allProperties;
    return uniqueSorted(source.map((p) => p.neighborhood));
  }, [allProperties, filters.city]);

  return (
    <main className="min-h-screen bg-background-50">
      <Navbar />

      {/* Page header */}
      <section className="bg-secondary-900 py-8 md:py-12 px-4 md:px-8">
        <div>
          <button
            onClick={() => navigate('/')}
            className="text-background-400 text-sm hover:text-primary-400 transition-colors mb-3 flex items-center gap-1"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line text-xs"></i>
            </span>
            Voltar para início
          </button>
          <h1 className="text-2xl md:text-3xl font-bold text-background-50">
            Imóveis à Venda e para Locação
          </h1>
          <p className="text-background-400 text-sm mt-2">
            {filteredProperties.length} imóveis encontrados no Oeste Catarinense
          </p>
        </div>
      </section>

      <div className="px-4 md:px-8 py-8 md:py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar filters */}
          <aside className="w-full lg:w-72 flex-shrink-0">
            <div className="bg-background-100 rounded-lg border border-background-200/70 p-5 sticky top-24">
              <h3 className="text-sm font-bold text-secondary-900 mb-4 flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                  <i className="ri-filter-3-line"></i>
                </span>
                Filtros
              </h3>

              {/* Type filter */}
              <div className="mb-4">
                <label className="text-xs font-semibold text-foreground-700 block mb-2">Tipo de Imóvel</label>
                <select
                  value={filters.type}
                  onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                  className="w-full appearance-none bg-background-50 border border-background-300/60 rounded-md px-3 py-2 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400"
                >
                  <option value="">Todos os tipos</option>
                  {types.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              {/* Purpose filter */}
              <div className="mb-4">
                <label className="text-xs font-semibold text-foreground-700 block mb-2">Finalidade</label>
                <select
                  value={filters.purpose}
                  onChange={(e) => setFilters({ ...filters, purpose: e.target.value })}
                  className="w-full appearance-none bg-background-50 border border-background-300/60 rounded-md px-3 py-2 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400"
                >
                  <option value="">Venda e Locação</option>
                  {purposes.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              {/* City filter */}
              <div className="mb-4">
                <label className="text-xs font-semibold text-foreground-700 block mb-2">Cidade</label>
                <select
                  value={filters.city}
                  onChange={(e) => {
                    const city = e.target.value;
                    const stillValid = !city || allProperties.some((p) => p.city === city && p.neighborhood === filters.neighborhood);
                    setFilters({ ...filters, city, neighborhood: stillValid ? filters.neighborhood : '' });
                  }}
                  className="w-full appearance-none bg-background-50 border border-background-300/60 rounded-md px-3 py-2 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400"
                >
                  <option value="">Todas as cidades</option>
                  {cities.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              {/* Neighborhood filter */}
              <div className="mb-4">
                <label className="text-xs font-semibold text-foreground-700 block mb-2">Bairro</label>
                <select
                  value={filters.neighborhood}
                  onChange={(e) => setFilters({ ...filters, neighborhood: e.target.value })}
                  className="w-full appearance-none bg-background-50 border border-background-300/60 rounded-md px-3 py-2 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400"
                >
                  <option value="">Todos os bairros</option>
                  {neighborhoods.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>

              {/* Price range */}
              <div className="mb-5">
                <label className="text-xs font-semibold text-foreground-700 block mb-2">Faixa de Preço</label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="number"
                    placeholder="Mínimo"
                    value={filters.minPrice}
                    onChange={(e) => setFilters({ ...filters, minPrice: e.target.value })}
                    className="w-full bg-background-50 border border-background-300/60 rounded-md px-3 py-2 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 placeholder:text-foreground-400"
                  />
                  <input
                    type="number"
                    placeholder="Máximo"
                    value={filters.maxPrice}
                    onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })}
                    className="w-full bg-background-50 border border-background-300/60 rounded-md px-3 py-2 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 placeholder:text-foreground-400"
                  />
                </div>
              </div>

              <button
                onClick={() => setFilters({ type: '', purpose: '', city: '', neighborhood: '', minPrice: '', maxPrice: '' })}
                className="w-full text-xs font-semibold text-primary-500 hover:text-primary-600 transition-colors flex items-center justify-center gap-1 py-2"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-refresh-line"></i>
                </span>
                Limpar filtros
              </button>
            </div>
          </aside>

          {/* Results */}
          <div className="flex-1">
            {/* Sort bar */}
            <div className="flex items-center justify-between mb-5">
              <p className="text-sm text-foreground-600">
                <span className="font-semibold text-foreground-900">{filteredProperties.length}</span> imóveis encontrados
              </p>
              <div className="flex items-center gap-2">
                <label className="text-xs text-foreground-600 hidden sm:inline">Ordenar por:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="appearance-none bg-background-100 border border-background-300/60 rounded-md px-3 py-2 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400"
                >
                  <option value="relevance">Relevância</option>
                  <option value="price-asc">Menor preço</option>
                  <option value="price-desc">Maior preço</option>
                </select>
              </div>
            </div>

            {/* Grid */}
            {filteredProperties.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {filteredProperties.map((property) => (
                  <PropertyCard key={property.id} property={property} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <span className="w-12 h-12 flex items-center justify-center mx-auto mb-4 text-background-400">
                  <i className="ri-search-line text-2xl"></i>
                </span>
                <h3 className="text-lg font-semibold text-foreground-700 mb-1">Nenhum imóvel encontrado</h3>
                <p className="text-sm text-foreground-500">Tente ajustar os filtros para ver mais resultados.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </main>
  );
}
